import { useEffect } from 'react';
import { css } from '@emotion/react';
import { navigateToUrl } from '@frontend/auth';
import { useTranslation } from '@frontend/i18n';
import { sentry } from '@frontend/tracking';
import { theme } from '@frontend/theme';
import { Heading, Text } from '@frontend/design-system';
import { FormWrap } from './form-wrap';

type Props = { redirectTo: string };

export const NativeRedirectComplete = ({ redirectTo }: Props) => {
  const { t } = useTranslation('SignIn');

  // Security: `redirectTo` is intentionally passed via TanStack router history state, NOT a URL
  // query param, so an attacker can't craft a link that auto-fires a deep link on visit. The value
  // has already been validated as either a direct `weave://` deep link or an OAuth URL whose
  // `redirect_uri` param is a validated `weave://` URL pointing at an allowlisted Weave host in
  // `handlePostAuthRedirect` (and re-validated in the route's `beforeLoad` as defense in depth),
  // so it's safe to hand to `navigateToUrl` here.
  useEffect(() => {
    fireDeepLink(redirectTo, 'native deep link auto-trigger');
    // make sure to only fire on mount, even if the component re-renders for some reason (e.g. parent route's loader runs again and updates state)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <FormWrap>
      <Heading
        css={css`
          margin-bottom: ${theme.spacing(2)};
        `}
      >
        {t("You're signed in")}
      </Heading>
      <Text
        css={css`
          margin-bottom: ${theme.spacing(4)};
        `}
      >
        {t('You can close this window.')}
      </Text>
      <Text
        css={css`
          margin-bottom: ${theme.spacing(4)};
        `}
      >
        {t("If the Weave app didn't open, try signing in again from the Weave app.")}
      </Text>
    </FormWrap>
  );
};

// wrap the redirect to the weave:// deep link in a function that logs duplicate firings
// we might have some kind of bug causing multiple firings
// this is likely temporary
function fireDeepLink(redirectTo: string, reason: string) {
  const now = Date.now();
  // Hash the URL so sensitive OAuth params (code/state) are never persisted to storage
  const urlHash = fnv1aHash(redirectTo).toString(16);
  try {
    const stored = localStorage.getItem('native-redirect:last-trigger');
    if (stored) {
      try {
        const { hash, timestamp } = JSON.parse(stored) as { hash: string; timestamp: number };
        // Same URL hash fired within 10s = observable duplicate (remount, multi-tab, or retry)
        if (hash === urlHash && now - timestamp < 10_000) {
          sentry.warn({
            topic: 'auth',
            team: 'users-infra',
            error: `Native deep link fired again after first trigger`,
            addContext: {
              name: 'NativeRedirectComplete',
              context: { reason, deltaMs: now - timestamp },
            },
          });
        }
      } catch {
        // ignore malformed entry
      }
    }
    localStorage.setItem('native-redirect:last-trigger', JSON.stringify({ hash: urlHash, timestamp: now }));
  } catch {
    // storage unavailable (private browsing, quota error, etc.) — proceed with redirect anyway
  }
  navigateToUrl(redirectTo, { reason });
}

function fnv1aHash(str: string): number {
  let hash = 0x811c9dc5; // FNV offset basis
  for (let i = 0; i < str.length; i++) {
    hash ^= str.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193); // FNV prime
  }
  return hash >>> 0; // Unsigned 32-bit integer
}
